import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { ArticleSchema } from '@/components/feature/SeoSchema';

export default function BasetaoProsCons() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen">
      <Header />
      <ArticleSchema slug="basetao-pros-cons" />
      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-foreground-900 transition-colors whitespace-nowrap">Home</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-900 font-medium">Pros and Cons</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase text-accent-700 bg-accent-100 rounded-full">Analysis</span>
              <span className="text-xs text-foreground-500">Updated August 2026</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-5">
              Basetao Pros and Cons 2026 — Honest Platform Analysis
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              Every shopping platform has strengths and weaknesses. This unbiased <strong>Basetao pros and cons</strong> analysis breaks down exactly what the platform does well and where it falls short, helping you decide whether Basetao is the right choice for your international shopping needs.
            </p>
          </header>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Basetao Advantages</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: 'Massive Product Selection', desc: 'Access to millions of products across every imaginable category.' },
                  { title: 'Spreadsheet Discovery System', desc: 'Unique organized product research tool saves hours of browsing.' },
                  { title: 'Warehouse Consolidation', desc: 'Combine multiple orders into one shipment for huge savings.' },
                  { title: 'QC Photo Inspection', desc: 'See actual product photos before international shipping.' },
                  { title: 'Competitive Shipping Rates', desc: 'Multiple carrier options with rates competitive with alternatives.' },
                  { title: 'Active Community', desc: 'Large user base provides reviews, tips, and shared experiences.' },
                ].map((item) => (
                  <div key={item.title} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-check-line text-primary-500"></i>
                      <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Basetao Disadvantages</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: 'Support Response Times', desc: 'Customer service can be slow during peak shopping seasons.' },
                  { title: 'Language Barriers', desc: 'Some product listings and communications may require translation.' },
                  { title: 'No Direct Seller Contact', desc: 'All communication goes through Basetao, which adds processing time.' },
                  { title: 'Shipping Uncertainty', desc: 'International delivery times can vary significantly by carrier and destination.' },
                ].map((item) => (
                  <div key={item.title} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-close-line text-accent-500"></i>
                      <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">FAQ</h2>
              <div className="space-y-5">
                {[
                  { q: 'Is Basetao worth using despite the disadvantages?', a: 'For most international shoppers, yes. The advantages — particularly the Spreadsheet discovery system and warehouse consolidation savings — significantly outweigh the platform\'s limitations for the majority of use cases.' },
                ].map((faq, idx) => (
                  <div key={idx}><h3 className="text-base font-semibold text-foreground-900 mb-1.5">{faq.q}</h3><p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p></div>
                ))}
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors duration-300 whitespace-nowrap"><i className="ri-home-4-line"></i>Back to Homepage</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-900 bg-background-100 hover:bg-background-200 border border-background-300/60 rounded-full transition-colors duration-300 whitespace-nowrap">Visit Main Website<i className="ri-external-link-line"></i></a>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}